/**
 * Theming props for the Community Posts Widget.
 * These map to CSS custom properties used internally.
 */
export interface CommunityPostsThemingProps {
  /** Primary color */
  postsPrimaryColor?: string;
  /** Container background color */
  postsBackgroundColor?: string;
  /** Primary text color */
  postsTextColor?: string;
  /** Secondary text color */
  postsSecondaryTextColor?: string;
  /** Border color */
  postsBorderColor?: string;
  /** Card background */
  postsCardBg?: string;
  /** Card border radius */
  postsCardRadius?: string;
  /** Card box shadow */
  postsCardShadow?: string;
  /** Card padding */
  postsCardPadding?: string;
  /** Avatar size */
  postsAvatarSize?: string;
  /** Avatar border radius */
  postsAvatarRadius?: string;
  /** Post title font size */
  postsTitleSize?: string;
  /** Post title font weight */
  postsTitleWeight?: string;
  /** Post body font size */
  postsBodySize?: string;
  /** Meta text font size */
  postsMetaSize?: string;
  /** Font family */
  postsFontFamily?: string;
  /** Gap between posts */
  postsGap?: string;
  /** Tag background */
  postsTagBg?: string;
  /** Tag text color */
  postsTagColor?: string;
  /** Tag border radius */
  postsTagRadius?: string;
  /** Action button background */
  postsActionBg?: string;
  /** Action button text color */
  postsActionColor?: string;
  /** Action button hover background */
  postsActionHoverBg?: string;
  /** Like button color */
  postsLikeColor?: string;
  /** Transition duration */
  postsTransitionDuration?: string;
}

/**
 * Core configuration props for the Community Posts Widget.
 */
export interface CommunityPostsWidgetProps extends CommunityPostsThemingProps {
  /** Base URL of the Legion application (required) */
  baseUrl: string;
  /** JWT auth token for authenticated features */
  authToken?: string;
  /** Number of posts per page (5-50). Default: 10 */
  pageSize?: number;
  /** Filter mode. Default: "recent" */
  filter?: "recent" | "popular" | "unanswered";
  /** Channel/group ID to filter posts */
  channelId?: string;
  /** Show filter bar. Default: true */
  showFilters?: boolean;
  /** Show action buttons (like, comment). Default: true */
  showActions?: boolean;
  /** Current user ID */
  userId?: string;
  /** Current username */
  username?: string;
  /** Theme preset. Default: "light" */
  theme?: "light" | "dark";
  /** Widget height CSS value. Default: "800px" */
  height?: string;
  /** Widget width CSS value. Default: "100%" */
  width?: string;
  /** Additional CSS class name for the container */
  className?: string;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
  /** Callback when the widget iframe has loaded */
  onLoad?: (detail: { widget: string; theme: string }) => void;
  /** Callback when the widget iframe fails to load */
  onError?: (message: string) => void;
}

/**
 * Mapping from camelCase theming prop names to kebab-case query params.
 */
export const POSTS_THEMING_PROP_TO_PARAM: Record<
  keyof CommunityPostsThemingProps,
  string
> = {
  postsPrimaryColor: "posts-primary-color",
  postsBackgroundColor: "posts-background-color",
  postsTextColor: "posts-text-color",
  postsSecondaryTextColor: "posts-secondary-text-color",
  postsBorderColor: "posts-border-color",
  postsCardBg: "posts-card-bg",
  postsCardRadius: "posts-card-radius",
  postsCardShadow: "posts-card-shadow",
  postsCardPadding: "posts-card-padding",
  postsAvatarSize: "posts-avatar-size",
  postsAvatarRadius: "posts-avatar-radius",
  postsTitleSize: "posts-title-size",
  postsTitleWeight: "posts-title-weight",
  postsBodySize: "posts-body-size",
  postsMetaSize: "posts-meta-size",
  postsFontFamily: "posts-font-family",
  postsGap: "posts-gap",
  postsTagBg: "posts-tag-bg",
  postsTagColor: "posts-tag-color",
  postsTagRadius: "posts-tag-radius",
  postsActionBg: "posts-action-bg",
  postsActionColor: "posts-action-color",
  postsActionHoverBg: "posts-action-hover-bg",
  postsLikeColor: "posts-like-color",
  postsTransitionDuration: "posts-transition-duration",
};

